import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";

const MemberNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const { overlayData, setOverlayName, triggerPageRefresh } = useStateContext();
  const { showSnackbar } = useStateContext();

  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_NEW
      ? "New Member"
      : "Edit Member",
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const isNewMember = overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_NEW;

  // Edit lock for existing members only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "member",
    isNewMember ? null : overlayData.Member?.id,
  );

  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const [name, setName] = useState(
    isNewMember ? "" : overlayData.Member.Name || "",
  );

  const [email, setEmail] = useState(
    isNewMember ? "" : overlayData.Member.Email || "",
  );

  const [role, setRole] = useState(
    isNewMember ? "" : overlayData.Member.Role || "",
  );

  const [generatedPassword, setGeneratedPassword] = useState("");

  function saveChanges() {
    // Validation
    if (!name.trim()) {
      setNameError(true);
      showSnackbar("Name is required", theme, "error");
      return;
    }

    if (!email.trim()) {
      setEmailError(true);
      showSnackbar("Email is required", theme, "error");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError(true);
      showSnackbar("Invalid email format", theme, "error");
      return;
    }

    setIsSavingChanges(true);
    setIsEditable(false);

    const data = {
      id: overlayData.MemberID,
      name: name,
      email: email,
      role: role,
    };

    overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_NEW
      ? addNewMember(data)
      : updateMember(data);
  }

  function addNewMember(data) {
    api
      .post("/members", data)
      .then((response) => {
        showSnackbar("Member created successfully", theme, "success");
        setGeneratedPassword(response.data.temporaryPassword);

        // Emit socket event for real-time updates
        if (socket) {
          const newMemberId = response?.data?.member?.id ?? response?.data?.id;
          if (newMemberId) {
            socket.emit("member:saved", {
              modelType: "Member",
              recordId: newMemberId,
              changeAction: "Created",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }
        }

        triggerPageRefresh();
        // Don't close the overlay yet - show the password first
      })
      .catch((err) => {
        console.error(err);
        showSnackbar(
          err.response?.data?.error || "Error creating member",
          theme,
          "error",
        );
        setIsEditable(true);
      })
      .finally(() => {
        setIsSavingChanges(false);
      });
  }

  function updateMember(data) {
    api
      .put(`/members/${data.id}`, data)
      .then(() => {
        showSnackbar("Member updated successfully", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("member:saved", {
            modelType: "Member",
            recordId: data.id,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh();
        setOverlayName("");
      })
      .catch((err) => {
        console.error(err);
        showSnackbar(
          err.response?.data?.error || "Error updating member",
          theme,
          "error",
        );
        setIsEditable(true);
      })
      .finally(() => {
        setIsSavingChanges(false);
      });
  }

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setOverlayName("");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setOverlayName]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "800px",
          maxHeight: "80%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.5rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => setOverlayName("")}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_NEW
                ? "New Member"
                : "Edit Member"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: "40%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={() => setOverlayName("")}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                endIcon={<DisabledByDefaultRoundedIcon />}
                onClick={() => setOverlayName("")}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Discard
              </Button>
            )}

            {isMobile ? (
              <Tooltip
                title={
                  isSavingChanges
                    ? "Saving..."
                    : !!generatedPassword
                      ? "Password shown"
                      : "Save"
                }
                placement="top"
              >
                <span>
                  <IconButton
                    disabled={isSavingChanges || !!generatedPassword}
                    color="primary"
                    onClick={saveChanges}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor:
                          theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Button
                loading={isSavingChanges}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={saveChanges}
                disabled={isSavingChanges || !!generatedPassword}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Save
              </Button>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Member Information
              </Typography>
            </Grid>

            {generatedPassword && (
              <>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.success.light,
                      borderRadius: "8px",
                      border: `2px solid ${theme.palette.success.main}`,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      ✅ Member Created Successfully!
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Temporary Password (share this securely):
                    </Typography>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: theme.palette.background.default,
                        borderRadius: "4px",
                        fontFamily: "monospace",
                        fontSize: "1.1rem",
                        letterSpacing: "0.05em",
                        wordBreak: "break-all",
                        mt: 1,
                      }}
                    >
                      {generatedPassword}
                    </Box>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 1, color: theme.palette.error.main }}
                    >
                      ⚠️ This password will only be shown once. Please copy it
                      before closing.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedPassword);
                        showSnackbar(
                          "Password copied to clipboard",
                          theme,
                          "success",
                        );
                      }}
                      sx={{ mt: 2 }}
                    >
                      Copy Password
                    </Button>
                  </Box>
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="Name"
                label="Name"
                required
                error={nameError}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(event.target.value.trim() === "");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={
                  !isEditable ||
                  overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_EDIT
                }
                size="small"
                id="Email"
                label="Email"
                required
                error={emailError}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setEmailError(event.target.value.trim() === "");
                }}
                helperText={
                  overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_EDIT
                    ? "Email cannot be changed"
                    : ""
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id="Role"
                label="Role"
                value={role}
                onChange={(event) => {
                  setRole(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Member">Member</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default MemberNewEdit;
